import React, { useEffect, useState, useMemo, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
    fetchQuery,
    deleteQuery,
} from "../../Redux/Reducers/UserFeedbackReducerList";
import {
    Box,
    IconButton,
    Dialog,
    DialogActions,
    DialogContent,
    DialogContentText,
    DialogTitle,
    Button,
    Alert,
    CircularProgress,
    TextField,
    TablePagination,
    InputAdornment,
} from "@mui/material";
import { DataGrid } from "@mui/x-data-grid";
import { Link, useSearchParams } from "react-router-dom";
import PageHeader1 from "../../components/common/PageHeader1";
import * as XLSX from 'xlsx';
import axios from 'axios';
import { toast } from 'react-toastify';
import { convertISTToUTCForAPI, formatUTCDate } from '../../utils/dateUtils';
import { getIntParam, getStringParam, setIfTruthy } from '../../utils/urlState';

function AstroSupportDetails() {
    const dispatch = useDispatch();
    const [searchParams, setSearchParams] = useSearchParams();
    const { status, error } = useSelector((state) => state.UserFeedbackReducerList || {});
    const FeedbackData = useSelector((state) => state.UserFeedbackReducerList?.data || []);
    const pagination = useSelector((state) => state.UserFeedbackReducerList?.pagination);
    const [columnVisibilityModel, setColumnVisibilityModel] = useState({});
    const [paginationModel, setPaginationModel] = useState({
        page: getIntParam(searchParams, "page", 0),
        pageSize: getIntParam(searchParams, "pageSize", 10),
    });

    // Helper function to get today's date in YYYY-MM-DD format
    const getTodayDate = () => {
        const today = new Date();
        const year = today.getFullYear();
        const month = String(today.getMonth() + 1).padStart(2, '0');
        const day = String(today.getDate()).padStart(2, '0');
        return `${year}-${month}-${day}`;
    };

    const todayDate = getTodayDate();

    const [searchQuery, setSearchQuery] = useState(getStringParam(searchParams, "search", ""));
    const [appliedSearchQuery, setAppliedSearchQuery] = useState(getStringParam(searchParams, "search", ""));
    const [startDate, setStartDate] = useState(getStringParam(searchParams, "startDate", ""));
    const [appliedStartDate, setAppliedStartDate] = useState(getStringParam(searchParams, "startDate", ""));
    const [endDate, setEndDate] = useState(getStringParam(searchParams, "endDate", ""));
    const [appliedEndDate, setAppliedEndDate] = useState(getStringParam(searchParams, "endDate", ""));
    const [lastAction, setLastAction] = useState(null); // "apply" to track when Apply Filters was clicked
    const [isExporting, setIsExporting] = useState(false);
    const isInitialMount = useRef(true);
    const lastParamsRef = useRef(null);
    const didInitUrlSyncRef = useRef(false);
    const applySawLoadingRef = useRef(false);
    const [isApplyingFilters, setIsApplyingFilters] = useState(false);

    const [selectedFeedbackId, setSelectedFeedbackId] = useState(null);
    const [open, setOpen] = useState(false);

    // Apply Filters Handler - Applies search and date filters together
    const handleApplyFilters = () => {
        setIsApplyingFilters(true);
        applySawLoadingRef.current = false;
        setAppliedSearchQuery(searchQuery.trim());
        setAppliedStartDate(startDate);
        setAppliedEndDate(endDate);
        setLastAction("apply");
        setPaginationModel(prev => ({ ...prev, page: 0 }));
    };

    useEffect(() => {
        const { page, pageSize } = paginationModel;
        let limit = Number(pageSize);
        if (isNaN(limit)) {
            limit = 10000; // Use fixed default
        }

        const params = {
            page: page + 1,
            limit,
        };

        // Date logic: Only send dates if explicitly applied
        if (appliedStartDate && appliedEndDate) {
            // Dates were explicitly applied - use them
            params.startDate = convertISTToUTCForAPI(appliedStartDate, 'start');
            params.endDate = convertISTToUTCForAPI(appliedEndDate, 'end');
        }
        // If no dates applied, don't send dates

        // Add search parameters if search query exists
        if (appliedSearchQuery && appliedSearchQuery.trim()) {
            params.search = appliedSearchQuery;
        }

        // Prevent duplicate API calls by comparing params
        const paramsString = JSON.stringify(params);
        if (lastParamsRef.current === paramsString && !isInitialMount.current) {
            return; // Skip if params haven't changed
        }

        lastParamsRef.current = paramsString;
        isInitialMount.current = false;

        dispatch(fetchQuery(params));
    }, [paginationModel, dispatch, appliedSearchQuery, appliedStartDate, appliedEndDate, lastAction]);

    // Apply button should stay disabled until request completes.
    useEffect(() => {
        if (!isApplyingFilters) return;
        const isLoading = status === "loading";
        if (isLoading) {
            applySawLoadingRef.current = true;
            return;
        }
        if (!isLoading && applySawLoadingRef.current) {
            applySawLoadingRef.current = false;
            setIsApplyingFilters(false);
        }
    }, [status, isApplyingFilters]);

    // Keep URL in sync so Back restores pagination/filters.
    useEffect(() => {
        if (!didInitUrlSyncRef.current) {
            didInitUrlSyncRef.current = true;
            return;
        }
        const params = new URLSearchParams();
        params.set("page", String(paginationModel.page));
        params.set("pageSize", String(paginationModel.pageSize));
        setIfTruthy(params, "search", appliedSearchQuery);
        setIfTruthy(params, "startDate", appliedStartDate);
        setIfTruthy(params, "endDate", appliedEndDate);
        setSearchParams(params, { replace: true });
    }, [
        paginationModel.page,
        paginationModel.pageSize,
        appliedSearchQuery,
        appliedStartDate,
        appliedEndDate,
        setSearchParams,
    ]);

    const handleClickOpen = (feedbackId) => {
        setSelectedFeedbackId(feedbackId);
        setOpen(true);
    };

    const handleClose = () => {
        setOpen(false);
    };

    const handleChangePage = (event, newPage) => {
        setPaginationModel({ ...paginationModel, page: newPage });
    };

    const handleChangeRowsPerPage = (event) => {
        const newPageSize = Number(event.target.value);
        setPaginationModel({ page: 0, pageSize: newPageSize });
    };

    const handleResetFilter = () => {
        setSearchQuery('');
        setAppliedSearchQuery('');
        setStartDate("");
        setEndDate("");
        setAppliedStartDate("");
        setAppliedEndDate("");
        setLastAction(null);
        setPaginationModel(prev => ({ ...prev, page: 0 }));
    };

    const exportToExcel = async () => {
        setIsExporting(true);
        try {
            const token = localStorage.getItem("astroVani-admin-token");
            const params = new URLSearchParams();

            // Send dates only if explicitly provided
            if (appliedStartDate) {
                params.append('startDate', convertISTToUTCForAPI(appliedStartDate, 'start'));
            }
            if (appliedEndDate) {
                params.append('endDate', convertISTToUTCForAPI(appliedEndDate, 'end'));
            }

            // Include search parameters in export if search was applied
            if (appliedSearchQuery && appliedSearchQuery.trim()) {
                params.append('search', appliedSearchQuery);
            }

            const url = `${process.env.REACT_APP_BASEURL}/admin/query?${params.toString()}`;
            const response = await axios.get(url, {
                headers: { Authorization: token },
            });

            const allData = response?.data?.data || [];
            const exportData = allData.map((item, index) => ({
                'S.No': index + 1,
                'User Name': item?.name || '-',
                'Email': item?.email || '-',
                'Phone Number': item?.mobile || '-',
                'Query': item?.query || '-',
                'Created Date': formatUTCDate(item?.createdAt, { format: 'date', separator: '/' }),
                'Created Time': formatUTCDate(item?.createdAt, { format: 'time', showSeconds: true }),
            }));

            const ws = XLSX.utils.json_to_sheet(exportData);
            const wb = XLSX.utils.book_new();
            XLSX.utils.book_append_sheet(wb, ws, 'Astro Feedback');
            XLSX.writeFile(wb, `Astro_Feedback_${new Date().toISOString().split('T')[0]}.xlsx`);
            toast.success('Export successful!');
        } catch (error) {
            console.error('Export error:', error);
            toast.error('Failed to export data');
        } finally {
            setIsExporting(false);
        }
    };

    const handleDelete = () => {
        if (selectedFeedbackId) {
            dispatch(deleteQuery(selectedFeedbackId));
            const { page, pageSize } = paginationModel;
            let limit = Number(pageSize);
            if (isNaN(limit)) {
                limit = 10000;
            }
            dispatch(fetchQuery({
                page: page + 1,
                limit,
                search: appliedSearchQuery || undefined,
                startDate: appliedStartDate ? convertISTToUTCForAPI(appliedStartDate, 'start') : undefined,
                endDate: appliedEndDate ? convertISTToUTCForAPI(appliedEndDate, 'end') : undefined,
            }));
            setOpen(false);
        }
    };

    const columns = [
        {
            field: "sono",
            headerName: "S.No",
            width: 80,
            headerAlign: 'center',
            align: 'center',
        },
        {
            field: "actions",
            headerName: "Actions",
            width: 140,
            sortable: false,
            filterable: false,
            headerAlign: 'center',
            align: 'center',
            renderCell: ({ row }) => (
                <Box sx={{ display: 'flex', gap: 0.5, justifyContent: 'center', alignItems: 'center', width: '100%', height: '100%' }}>
                    <IconButton
                        size="small"
                        component={Link}
                        to={`/AstroView-detail/${row.id}`}
                        title="View Details"
                        sx={{
                            color: '#d32f2f',
                            '&:hover': {
                                backgroundColor: 'rgba(211, 47, 47, 0.08)',
                            },
                        }}
                    >
                        <i className="icofont-eye" style={{ fontSize: '18px' }}></i>
                    </IconButton>
                    <IconButton
                        size="small"
                        onClick={() => handleClickOpen(row.id)}
                        title="Delete Query"
                        sx={{
                            color: '#d32f2f',
                            '&:hover': {
                                backgroundColor: 'rgba(211, 47, 47, 0.08)',
                            },
                        }}
                    >
                        <i className="icofont-ui-delete" style={{ fontSize: '18px' }}></i>
                    </IconButton>
                </Box>
            ),
        },
        { field: "name", headerName: "User Name", width: 200 },
        { field: "email", headerName: "Email", width: 250 },
        { field: "mobile", headerName: "Phone Number", width: 150 },
        {
            field: "feedback",
            headerName: "Query",
            width: 300,
            renderCell: ({ row }) => (
                <Box
                    sx={{
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap',
                        maxWidth: '100%',
                    }}
                    title={row?.feedback || ''}
                >
                    {row?.feedback || '-'}
                </Box>
            ),
        },
        { field: 'createdAt', headerName: 'Created At', width: 200, type: 'dateTime' },
    ];

    const rows = useMemo(() => {
        return Array.isArray(FeedbackData) ? FeedbackData.map((feedback, index) => ({
            sono: paginationModel.page * paginationModel.pageSize + index + 1,
            id: feedback?._id,
            name: feedback?.name || '-',
            email: feedback?.email || '-',
            mobile: feedback?.mobile || '-',
            feedback: feedback?.query || '-',
            createdAt: feedback?.createdAt ? new Date(feedback?.createdAt) : null,
        })) : [];
    }, [FeedbackData, paginationModel]);

    const loading = status === 'loading';

    return (
        <div className="body d-flex">
            <div className="container-xxl">
                <PageHeader1 pagetitle="Astro Feedback List" />

                {status === 'failed' && error && (
                    <Alert
                        severity="error"
                        sx={{
                            mb: 2,
                            borderRadius: '8px',
                        }}
                    >
                        Error: {error}
                    </Alert>
                )}

                {/* Custom Toolbar */}
                <div className="card mb-3" style={{
                    borderRadius: '12px',
                    boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
                    border: 'none'
                }}>
                    <div className="card-body" style={{ padding: '16px 20px' }}>
                        {/* Search and Filter Toolbar - Single Line */}
                        <Box sx={{ width: '100%' }}>
                            <Box display="flex" alignItems="center" gap={1} flexWrap="wrap">
                                <TextField
                                    placeholder="Search..."
                                    variant="outlined"
                                    size="small"
                                    value={searchQuery}
                                    onChange={(e) => setSearchQuery(e.target.value)}
                                    onKeyPress={(e) => { if (e.key === 'Enter') { handleApplyFilters(); } }}
                                    sx={{
                                        width: { xs: '120px', sm: '150px', md: '180px' },
                                        flexShrink: 0,
                                        background: 'var(--card-color)',
                                        borderRadius: '9px',
                                        '& .MuiInputBase-input': { fontSize: '13px', padding: '9px 12px' },
                                    }}
                                />
                                <TextField
                                    label="Start Date"
                                    type="date"
                                    variant="outlined"
                                    size="small"
                                    value={startDate || ""}
                                    onChange={(e) => setStartDate(e.target.value)}
                                    InputLabelProps={{ shrink: true }}
                                    sx={{
                                        width: { xs: '140px', sm: '160px', md: '180px' },
                                        flexShrink: 0,
                                        '& .MuiInputBase-input[type="date"]': {
                                            paddingRight: '8px',
                                            paddingLeft: '14px',
                                            position: 'relative',
                                            '&::-webkit-calendar-picker-indicator': {
                                                cursor: 'pointer',
                                                padding: '2px',
                                                marginLeft: '0px',
                                                marginRight: '4px',
                                                opacity: 0.7,
                                                position: 'absolute',
                                                right: 0,
                                                '&:hover': {
                                                    opacity: 1,
                                                },
                                            },
                                        },
                                        '& .MuiOutlinedInput-root': {
                                            paddingRight: '8px',
                                        },
                                    }}
                                />
                                <TextField
                                    label="End Date"
                                    type="date"
                                    variant="outlined"
                                    size="small"
                                    value={endDate || ""}
                                    onChange={(e) => setEndDate(e.target.value)}
                                    InputLabelProps={{ shrink: true }}
                                    sx={{
                                        width: { xs: '140px', sm: '160px', md: '180px' },
                                        flexShrink: 0,
                                        '& .MuiInputBase-input[type="date"]': {
                                            paddingRight: '8px',
                                            paddingLeft: '14px',
                                            position: 'relative',
                                            '&::-webkit-calendar-picker-indicator': {
                                                cursor: 'pointer',
                                                padding: '2px',
                                                marginLeft: '0px',
                                                marginRight: '4px',
                                                opacity: 0.7,
                                                position: 'absolute',
                                                right: 0,
                                                '&:hover': {
                                                    opacity: 1,
                                                },
                                            },
                                        },
                                        '& .MuiOutlinedInput-root': {
                                            paddingRight: '8px',
                                        },
                                    }}
                                />
                                <Button
                                    variant="contained"
                                    onClick={handleApplyFilters}
                                    disabled={(status === "loading") || isApplyingFilters || (!searchQuery.trim() && !startDate && !endDate)}
                                    sx={{
                                        borderRadius: "8px",
                                        textTransform: "none",
                                        backgroundColor: "var(--primary-color, #E7B242)",
                                        color: "#fff",
                                        height: 40,
                                        px: 2,
                                        flexShrink: 0,
                                        "&:hover": { backgroundColor: "var(--primary-color, #E7B242)", opacity: 0.9 },
                                        "&:disabled": {
                                            backgroundColor: "rgba(0, 0, 0, 0.12)",
                                            color: "rgba(0, 0, 0, 0.26)",
                                        },
                                    }}
                                >
                                    {isApplyingFilters ? "Applying..." : "Apply Filters"}
                                </Button>
                                <Button
                                    variant="outlined"
                                    onClick={handleResetFilter}
                                    sx={{
                                        borderRadius: '8px',
                                        textTransform: 'none',
                                        borderColor: 'var(--primary-color, #E7B242)',
                                        color: 'var(--primary-color, #E7B242)',
                                        height: 40,
                                        px: 2,
                                        '&:hover': { borderColor: 'var(--primary-color, #E7B242)', backgroundColor: 'rgba(231, 178, 66, 0.08)' },
                                        flexShrink: 0
                                    }}
                                >
                                    Reset
                                </Button>
                                <Button
                                    variant="contained"
                                    onClick={exportToExcel}
                                    disabled={isExporting}
                                    sx={{
                                        backgroundColor: 'var(--primary-color, #E7B242)',
                                        '&:hover': {
                                            backgroundColor: 'var(--primary-color, #E7B242)',
                                            opacity: 0.9,
                                        },
                                        '&:disabled': {
                                            backgroundColor: 'var(--primary-color, #E7B242)',
                                            opacity: 0.6,
                                        },
                                        borderRadius: '8px',
                                        textTransform: 'none',
                                        height: 40,
                                        px: 2,
                                        flexShrink: 0,
                                    }}
                                >
                                    {isExporting ? 'Exporting...' : 'Export'}
                                </Button>
                            </Box>
                        </Box>
                    </div>
                </div>

                <div className="row g-0 mb-3">
                    <div className="col-md-12">
                        <div className="card" style={{
                            borderRadius: '12px',
                            boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
                            border: 'none',
                            overflow: 'hidden'
                        }}>
                            <div className="card-body" style={{ padding: 0 }}>
                                <Box sx={{
                                    width: '100%',
                                    '& .MuiDataGrid-root': {
                                        border: 'none',
                                    },
                                    '& .MuiDataGrid-cell': {
                                        borderBottom: '1px solid #f0f0f0',
                                    },
                                    '& .MuiDataGrid-columnHeaders': {
                                        backgroundColor: '#f8f9fa',
                                        borderBottom: '2px solid #e9ecef',
                                        fontWeight: 600,
                                    },
                                    '& .MuiDataGrid-footerContainer': {
                                        display: 'none',
                                    },
                                    '& .MuiDataGrid-row:hover': {
                                        backgroundColor: '#f8f9fa',
                                    },
                                }}>
                                    <DataGrid
                                        columns={columns}
                                        rows={rows}
                                        loading={loading}
                                        disableSelectionOnClick
                                        columnVisibilityModel={columnVisibilityModel}
                                        onColumnVisibilityModelChange={(newModel) =>
                                            setColumnVisibilityModel(newModel)
                                        }
                                        paginationMode="server"
                                        hideFooterPagination
                                        autoHeight
                                        getRowId={(row) => row.id}
                                        sx={{
                                            '& .MuiDataGrid-cell:focus': {
                                                outline: 'none',
                                            },
                                            '& .MuiDataGrid-cell:focus-within': {
                                                outline: 'none',
                                            },
                                        }}
                                    />
                                    <TablePagination
                                        component="div"
                                        count={pagination?.totalDocs || 0}
                                        page={paginationModel.page}
                                        onPageChange={handleChangePage}
                                        rowsPerPage={paginationModel.pageSize}
                                        onRowsPerPageChange={handleChangeRowsPerPage}
                                        rowsPerPageOptions={[10, 25, 50]}
                                    />
                                </Box>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            {/* Delete Confirmation Dialog */}
            <Dialog open={open} onClose={handleClose}>
                <DialogTitle>Delete Confirmation</DialogTitle>
                <DialogContent>
                    <DialogContentText>
                        Are you sure you want to delete this query?
                    </DialogContentText>
                </DialogContent>
                <DialogActions>
                    <Button onClick={handleClose}>Cancel</Button>
                    <Button onClick={handleDelete} color="error" variant="contained">
                        Delete
                    </Button>
                </DialogActions>
            </Dialog>
        </div>
    );
}

export default AstroSupportDetails;
